// Angular
import { BehaviorSubject, Observable, takeUntil } from 'rxjs';
import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';

// Libraries

import { Select, Store } from '@ngxs/store';

// Services
import { TranslateService } from '@ngx-translate/core';
import { CoreService } from '../../../../shared/services';
import { AuthService } from '../../../auth';
import { UserService } from '../../services/user.service';

// Models
import { AppCleanup, DynamicMenuButton } from '../../../../shared/models';
import { UserModel } from '../../../../shared/models';
import { DynamicTableColumn } from '../../../../shared/models';

// Store
import { UserState } from '../../state/user.state';
import { UserActions } from '../../state/user.action';

// Enums
import { StatusType } from '../../../../shared/enums';

// Helpers
import { UtilityHelper } from '../../../../shared/helpers';

@Component({
	selector: 'user-list',
	templateUrl: './user-list.component.html',
	styleUrls: ['./user-list.component.scss'],
})
export class UserListComponent extends AppCleanup implements OnInit, OnDestroy {
	@Select(UserState.isLoaded) isLoaded$: Observable<boolean>;
	@Select(UserState.users) users$: Observable<UserModel[]>;
	@HostBinding('class') classes = 'component-container';
	protected mappedUsersVar: BehaviorSubject<UserModel[]> = new BehaviorSubject<UserModel[]>(null);
	mappedUsers$ = this.mappedUsersVar.asObservable();
	columnList: DynamicTableColumn[] = [
		{
			name: 'fullname',
			display: 'Name',
			width: 2,
		},
		{
			name: 'username',
			display: 'Username',
			hideOnSmallScreen: true,
			hideOnExtraSmallScreen: true,
			width: 2,
		},
		{
			name: 'email',
			display: 'Email',
			hideOnSmallScreen: true,
			hideOnExtraSmallScreen: true,
			width: 2,
		},
		{
			name: 'primaryRole',
			display: 'Primary Role',
			width: 1.5,
		},
		{
			name: 'companyCount',
			display: 'Companies',
			hideOnSmallScreen: true,
			hideOnExtraSmallScreen: true,
			width: 1,
		},
		{
			name: 'districtCount',
			display: 'Districts',
			hideOnMediumScreen: true,
			hideOnSmallScreen: true,
			hideOnExtraSmallScreen: true,
			width: 1,
		},
		{
			name: 'unitCount',
			display: 'Units',
			hideOnMediumScreen: true,
			hideOnSmallScreen: true,
			hideOnExtraSmallScreen: true,
			width: 1,
		},
	];
	menuButtons: DynamicMenuButton[] = [
		{
			matIcon: 'close',
			name: 'Delete',
			event: this.deleteUser.bind(this),
		},
	];
	initSpinner: boolean = false;

	constructor(
		private translateService: TranslateService,
		private coreService: CoreService,
		private store: Store,
		private authService: AuthService,
		private userService: UserService
	) {
		super();
	}

	ngOnInit() {
		this.isLoaded$.pipe(takeUntil(this.destroy)).subscribe((isLoaded) => {
			if (!isLoaded) {
				this.initSpinner = true;
				this.store.dispatch(new UserActions.GetAll({ showSpinner: false, showToast: true }));
			} else {
				this.users$.pipe(takeUntil(this.destroy)).subscribe((users) => {
					let usersCopy = UtilityHelper.deepCopy(users);
					usersCopy = usersCopy.filter((item) => item.isActive);
					usersCopy = this.mapPrimaryRole(usersCopy);
					this.mappedUsersVar.next(usersCopy);
				});
			}
		});
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	mapPrimaryRole(users: UserModel[]) {
		users.map((user) => {
			user.primaryRole = this.userService.getUserPrimaryRole(user.roles);
			user.fullname = user.firstName + ' ' + user.lastName;
			user.activeDisp = user.isActive ? 'Yes' : 'No';
			user.companyCount = user.companyIds.length;
			user.unitCount = user.unitUserIds.length;
			user.districtCount = user.districtUserIds.length;
		});

		return users;
	}

	deleteUser(user: UserModel) {
		// This method checks for logged in user role and role of the user being deleted to check permissions
		let doDelete = () => {
			this.store.dispatch(new UserActions.Delete({ id: user.id, showSpinner: true, showToast: true, showConfirm: true }));
		};

		let doWarning = () => {
			const message = `${this.translateService.instant('MODULE.USER.NOT_AUTHORIZED_TO_DELETE')}`;
			this.coreService.toast({ message: message, type: StatusType.WARNING });
		};

		let isCurrentUserTenantAdmin = false;
		let isCurrentUserSuperAdmin = false;
		let isCurrentUserAdmin = false;

		let isUserTenantAdmin = false;
		let isUserSuperAdmin = false;
		let isUserAdmin = false;

		if (user.roles.includes(6)) {
			isUserTenantAdmin = true;
		} else if (user.roles.includes(3)) {
			isUserSuperAdmin = true;
		} else if (user.roles.includes(2)) {
			isUserAdmin = true;
		}

		this.authService
			.getHighestRole()
			.pipe(takeUntil(this.destroy))
			.subscribe((currentUserHighestRole) => {
				if (currentUserHighestRole) {
					if (currentUserHighestRole === 6) {
						isCurrentUserTenantAdmin = true;
					} else if (currentUserHighestRole === 3) {
						isCurrentUserSuperAdmin = true;
					} else if (currentUserHighestRole === 2) {
						isCurrentUserAdmin = true;
					}

					if (isCurrentUserSuperAdmin) {
						if (isUserTenantAdmin) {
							doWarning();
						} else {
							doDelete();
						}
					} else if (isCurrentUserAdmin) {
						if (isUserTenantAdmin || isUserSuperAdmin) {
							doWarning();
						} else {
							doDelete();
						}
					} else {
						doDelete();
					}
				}
			});
	}
}
